import React, { useId } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FiCheck, FiX, FiClock, FiMinus, FiAlertTriangle } from 'react-icons/fi';

/**
 * États normalisés d’un indicateur de check.
 * Chaque état combine couleur + icône + libellé (accessibilité : jamais la couleur seule).
 */
const STATES = {
    done: { tone: 'success', icon: FiCheck, text: 'Terminé' },
    pending: { tone: 'warning', icon: FiClock, text: 'En attente' },
    na: { tone: 'neutral', icon: FiMinus, text: 'Non applicable' },
    error: { tone: 'danger', icon: FiX, text: 'Absent' },
    triggered: { tone: 'success', icon: FiAlertTriangle, text: 'Déclenché' },
    notTriggered: { tone: 'danger', icon: FiMinus, text: 'Non déclenché' },
    sent: { tone: 'success', icon: FiCheck, text: 'Envoyé' },
    notSent: { tone: 'neutral', icon: FiMinus, text: 'Non envoyé' },
    exists: { tone: 'success', icon: FiCheck, text: 'Importé' },
    missing: { tone: 'neutral', icon: FiX, text: 'Non importé' },
    created: { tone: 'success', icon: FiCheck, text: 'Créé' },
    toCreate: { tone: 'danger', icon: FiClock, text: 'À créer' },
    toSend: { tone: 'warning', icon: FiClock, text: 'À envoyer' },
    toCheck: { tone: 'neutral', icon: FiClock, text: 'À faire' },
};

const CheckIndicator = ({ shortLabel, label, state }) => {
    const tooltipId = useId();
    const config = STATES[state] || STATES.na;
    const Icon = config.icon;
    const description = `${label} : ${config.text}`;

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{description}</Tooltip>}>
            <span
                className={`ops-check ops-check--${config.tone}`}
                tabIndex={0}
                aria-label={description}
                role="img"
            >
                <Icon className="ops-check__icon" aria-hidden="true" />
                <span className="ops-check__label" aria-hidden="true">{shortLabel}</span>
            </span>
        </OverlayTrigger>
    );
};

const triggerState = (value) => {
    if (value === true) return 'triggered';
    if (value === false) return 'notTriggered';
    return 'na';
};

/** État de chaque étape du workflow forecast, lu directement dans `status` (une entrée par colonne). */
export const getForecastStepStates = (row) => {
    const status = row?.status;
    if (!status) return null;
    const triggered = status.is_triggered_forecast === true;
    return {
        forecast: status.forecast_exists ? 'exists' : 'missing',
        check: status.forecast_checked ? 'done' : 'toCheck',
        trigger: triggerState(status.is_triggered_forecast),
        claim: status.claim_exists ? 'created' : (triggered ? 'toCreate' : 'na'),
        email: status.forecast_email_sent ? 'sent' : (status.claim_exists ? 'toSend' : 'na'),
    };
};

/** Cellule d’une étape : icône + libellé d’état, info-bulle avec le nom de l’étape. */
export const StepStatusCell = ({ label, state }) => {
    const tooltipId = useId();
    if (!state) return <span className="ops-muted">—</span>;
    const config = STATES[state] || STATES.na;
    const Icon = config.icon;
    const description = `${label} : ${config.text}`;

    if (state === 'na') {
        return <span className="ops-muted" role="img" aria-label={description} title={description}>—</span>;
    }

    return (
        <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{description}</Tooltip>}>
            <span
                className={`ops-check ops-check--${config.tone} ops-check--step`}
                tabIndex={0}
                aria-label={description}
                role="img"
            >
                <Icon className="ops-check__icon" aria-hidden="true" />
                <span className="ops-check__label" aria-hidden="true">{config.text}</span>
            </span>
        </OverlayTrigger>
    );
};

const CheckStatusIndicators = ({ status, checkType = 'forecast' }) => {
    if (!status) return <span className="ops-muted">—</span>;

    if (checkType === 'observation') {
        return (
            <div className="ops-status-indicators">
                <CheckIndicator
                    shortLabel="Obs."
                    label="Observation effectuée"
                    state={status.observation_checked ? 'done' : 'pending'}
                />
                <CheckIndicator
                    shortLabel="Trigger"
                    label="Observation déclenchée"
                    state={triggerState(status.is_triggered_observation)}
                />
            </div>
        );
    }

    return (
        <div className="ops-status-indicators">
            <CheckIndicator
                shortLabel="Prév."
                label="Prévision existante"
                state={status.forecast_exists ? 'done' : 'error'}
            />
            <CheckIndicator
                shortLabel="Check"
                label="Check prévision effectué"
                state={status.forecast_checked ? 'done' : 'pending'}
            />
            <CheckIndicator
                shortLabel="Trigger"
                label="Prévision déclenchée"
                state={triggerState(status.is_triggered_forecast)}
            />
            <CheckIndicator
                shortLabel="Email"
                label="Email envoyé"
                state={status.forecast_email_sent ? 'sent' : 'notSent'}
            />
        </div>
    );
};

export default CheckStatusIndicators;
